import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Avatar } from "@/components/Avatar";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // "Welcome back, {name}" — the last user who signed in on this browser.
  const [lastUser, setLastUser] = useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && user !== false) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("last_login_user");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.email) {
          setLastUser(parsed);
          setEmail(parsed.email);
        }
      }
    } catch { /* corrupted cache — ignore, user will type manually */ }
  }, []);

  const forgetLastUser = () => {
    localStorage.removeItem("last_login_user");
    setLastUser(null);
    setEmail("");
    setPassword("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-slate-200 shadow-none p-8">
          <div className="mb-8">
            <img
              src="/tecticonism-wordmark-sm.png"
              alt="Tecticonism Studio"
              className="h-8 w-auto object-contain mb-6"
              data-testid="login-logo"
            />
            <h1 className="text-3xl font-heading font-semibold tracking-tight">
              {lastUser?.name ? "Welcome back" : "Sign in"}
            </h1>
            {lastUser?.name ? (
              <div className="mt-4 flex items-center gap-3" data-testid="login-welcome-back">
                <Avatar src={lastUser.avatar_url} name={lastUser.name} size="w-12 h-12" />
                <div className="min-w-0">
                  <div className="text-base font-heading font-semibold text-slate-900 truncate">
                    {lastUser.name.split(" ")[0]}
                  </div>
                  <button
                    type="button"
                    onClick={forgetLastUser}
                    className="text-xs text-brand hover:text-brand-hover underline underline-offset-2"
                    data-testid="login-not-you"
                  >
                    Not you? Sign in as a different user
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 mt-1">TS Nexus · Client · Projects · Finance</p>
            )}
          </div>
          <form onSubmit={onSubmit} className="space-y-4" autoComplete="off">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@tecticonismstudio.com"
                autoComplete="off"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              className="w-full bg-brand hover:bg-brand-hover text-white"
              disabled={loading}
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-xs text-slate-400 mt-6 text-center">
            © {new Date().getFullYear()} Tecticonism Studio LLC — All rights reserved.
          </p>
        </Card>
      </div>
      <div
        className="hidden lg:flex relative overflow-hidden items-center justify-start pl-16"
        data-testid="login-brand-panel"
      >
        {/* Cityscape illustration — cropped to the extreme right portion showing the scaffolding */}
        <img
          src="/login-cityscape.jpg"
          alt="Tecticonism Studio — architectural cityscape"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "right center" }}
        />
        {/* Soft cream wash keeps the text readable over any dense scaffolding */}
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "radial-gradient(circle at 20% 50%, rgba(255,245,229,0.7), transparent 55%)" }} />
        <div className="relative z-10 max-w-md text-left">
          <div className="text-4xl md:text-5xl uppercase tracking-[0.35em] font-bold text-brand leading-tight">
            TS Nexus
          </div>
          <h2 className="mt-4 font-heading text-2xl md:text-3xl font-semibold leading-tight text-brand-dark">
            Client&nbsp;•&nbsp;Projects&nbsp;•&nbsp;Finance
          </h2>
          <p className="mt-4 text-slate-700 text-sm md:text-base leading-relaxed max-w-sm">
            Track project stages, invoices, payments and outstanding balances with confidence.
          </p>
        </div>
      </div>
    </div>
  );
}
